"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Search, Target, Compass, ArrowUpRight, ChartBar as BarChart3, CircleCheck as CheckCircle2, MapPin, Building2, Zap, ArrowRight, Funnel as Filter } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis } from "recharts";
const searchTrendsData = [{
  month: "Jan",
  volume: 14200,
  growth: 12
}, {
  month: "Feb",
  volume: 15100,
  growth: 15
}, {
  month: "Mar",
  volume: 16800,
  growth: 18
}, {
  month: "Apr",
  volume: 17500,
  growth: 22
}, {
  month: "May",
  volume: 19200,
  growth: 25
}, {
  month: "Jun",
  volume: 21000,
  growth: 31
}];
const keywordOpportunities = [{
  id: "kw-1",
  keyword: "SEO consultant Cheltenham",
  intent: "High Commercial",
  volume: "880/mo",
  difficulty: "38/100 (Moderate)",
  opportunity: "High conversion intent with limited direct freelance competition in Gloucestershire."
}, {
  id: "kw-2",
  keyword: "Local SEO services Cheltenham",
  intent: "Transactional",
  volume: "1,200/mo",
  difficulty: "42/100 (Medium)",
  opportunity: "Strong demand from boutique retailers, professional services, and hospitality venues."
}, {
  id: "kw-3",
  keyword: "Cheltenham digital marketing strategy",
  intent: "Informational",
  volume: "640/mo",
  difficulty: "29/100 (Low)",
  opportunity: "Ideal for targeting growing tech and professional hubs along the M5 corridor."
}, {
  id: "kw-4",
  keyword: "B2B lead generation Cheltenham",
  intent: "Transactional",
  volume: "510/mo",
  difficulty: "45/100 (Medium)",
  opportunity: "High average deal value for professional firms in Promenade and Montpellier."
}];
const marketOpportunities = [{
  id: "op-1",
  title: "Google Map Pack & GMB Optimization",
  description: "34% of local searches in Cheltenham trigger Google Map Packs, but many local businesses lack optimized GBP categories and local schema.",
  impact: "+140% Local Visibility",
  tag: "High Priority"
}, {
  id: "op-2",
  title: "Hyper-Local Content Strategy",
  description: "Search queries in Cheltenham heavily favor localized neighborhood terms like Montpellier, Pitville, and Charlton Kings.",
  impact: "+85% Qualified Traffic",
  tag: "Quick Win"
}, {
  id: "op-3",
  title: "AI Search & Entity Building",
  description: "Generative search experiences are actively shaping local service discoveries. Structuring direct brand entities keeps you visible in AI Overviews.",
  impact: "Future-Proof Growth",
  tag: "Innovation"
}, {
  id: "op-4",
  title: "Technical Site Speed Overhaul",
  description: "Local competitors suffer from slow mobile load times. Technical precision gives immediate leverage in competitive search positions.",
  impact: "Lower Bounce Rates",
  tag: "Technical SEO"
}];
const neighboringLocations = [{
  name: "Wolverhampton",
  path: "/LocalSeoConsultantWolverhamptonPage",
  distance: "58 miles",
  focus: "Industrial & B2B SEO Strategy"
}, {
  name: "Milton Keynes",
  path: "/LocalSeoConsultantMiltonKeynesPage",
  distance: "65 miles",
  focus: "Tech Hub & Enterprise Growth"
}, {
  name: "Derby",
  path: "/LocalSeoConsultantDerbyPage",
  distance: "82 miles",
  focus: "Commercial & Local Search"
}, {
  name: "Northampton",
  path: "/LocalSeoConsultantNorthamptonPage",
  distance: "60 miles",
  focus: "E-commerce & Regional B2B"
}, {
  name: "Stoke on Trent",
  path: "/LocalSeoConsultantStokeOnTrentPage",
  distance: "90 miles",
  focus: "Local Authority & Professional Services"
}, {
  name: "Wakefield",
  path: "/LocalSeoConsultantWakefieldPage",
  distance: "140 miles",
  focus: "National & Regional Local SEO"
}];
const chartConfig = {
  volume: {
    label: "Search Volume",
    color: "var(--primary)"
  }
};
export default function CheltenhamAnalysis() {
  const [activeTab, setActiveTab] = useState<"all" | "commercial" | "transactional">("all");
  const shouldReduceMotion = useReducedMotion();
  const filteredKeywords = keywordOpportunities.filter(kw => {
    if (activeTab === "commercial") return kw.intent.toLowerCase().includes("commercial");
    if (activeTab === "transactional") return kw.intent.toLowerCase().includes("transactional");
    return true;
  });
  return <section id="cheltenhamlocalmarketanalysis" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs sm:text-sm font-medium rounded-full uppercase tracking-wider">
            <MapPin className="size-3.5 mr-1 text-primary inline" />
            Gloucestershire Search Insights
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            Cheltenham Local SEO Market Analysis
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-default">
            I have analyzed Cheltenham’s unique search dynamics to identify high-intent local keywords, competitor gaps, and immediate revenue opportunities for businesses in Gloucestershire.
          </p>
        </div>

        {/* Search Climate & Interactive Volume Trends */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-stretch">
          {/* Commentary Card */}
          <motion.div className="lg:col-span-5 flex flex-col" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }}>
            <Card className="h-full bg-card border-border shadow-md flex flex-col justify-between">
              <CardHeader className="space-y-3">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="border-primary/30 text-primary">
                    Market Intelligence
                  </Badge>
                  <TrendingUp className="size-5 text-accent" />
                </div>
                <CardTitle className="text-2xl font-semibold font-default text-foreground">
                  Cheltenham Search Climate
                </CardTitle>
                <CardDescription className="text-muted-foreground font-default leading-relaxed">
                  As an independent SEO consultant, I monitor regional search shifts directly. Cheltenham businesses are seeing a major shift toward high-intent micro-location searches.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 font-default">
                <div className="p-4 rounded-lg bg-muted/60 border border-border/50 space-y-2">
                  <div className="flex justify-between items-center text-sm font-medium">
                    <span className="text-foreground">Local Search Growth</span>
                    <span className="text-primary font-bold">+31% YoY</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Consistent growth in localized commercial queries across Cheltenham town center and surrounding business parks.
                  </p>
                </div>

                <div className="space-y-2 pt-2">
                  <div className="flex items-center text-sm text-foreground gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Direct direct consultant access (no account managers)</span>
                  </div>
                  <div className="flex items-center text-sm text-foreground gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Tailored Gloucestershire competitor benchmarking</span>
                  </div>
                  <div className="flex items-center text-sm text-foreground gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Focus on high-value lead acquisition</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Visual Trend Chart */}
          <motion.div className="lg:col-span-7 flex flex-col" initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }}>
            <Card className="h-full bg-card border-border shadow-md flex flex-col justify-between">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl font-semibold font-default">
                      Local Search Volume Trend
                    </CardTitle>
                    <CardDescription className="font-default">
                      Aggregate monthly organic search demand for Cheltenham commercial terms
                    </CardDescription>
                  </div>
                  <BarChart3 className="size-5 text-primary" />
                </div>
              </CardHeader>
              <CardContent className="pt-4 flex-1 flex flex-col justify-center">
                <ChartContainer config={chartConfig} className="h-[240px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={searchTrendsData} margin={{
                    top: 10,
                    right: 10,
                    left: -20,
                    bottom: 0
                  }}>
                      <defs>
                        <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="var(--primary)" stopOpacity={0.0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="month" stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis stroke="var(--muted-foreground)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={val => `${val / 1000}k`} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Area type="monotone" dataKey="volume" stroke="var(--primary)" strokeWidth={2.5} fillOpacity={1} fill="url(#colorVolume)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartContainer>
                <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground font-default">
                  <span>Source: Aggregated Search Console & Keyword Data</span>
                  <span className="text-primary font-medium">Updated Monthly</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* High Intent Keywords Analysis */}
        <div className="mb-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h3 className="text-2xl font-semibold text-foreground font-default flex items-center gap-2">
                <Search className="size-5 text-primary" />
                High-Intent Cheltenham Keywords
              </h3>
              <p className="text-muted-foreground text-sm font-default mt-1">
                Targeting local queries with high transactional value and manageable search difficulty.
              </p>
            </div>
            {/* Filter Buttons */}
            <div className="flex items-center gap-2 bg-muted p-1 rounded-lg border border-border self-start md:self-auto">
              <Button variant={activeTab === "all" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("all")} className="text-xs h-8">
                All Queries
              </Button>
              <Button variant={activeTab === "commercial" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("commercial")} className="text-xs h-8">
                Commercial
              </Button>
              <Button variant={activeTab === "transactional" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("transactional")} className="text-xs h-8">
                Transactional
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredKeywords.map((kw, i) => <motion.div key={kw.id} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.08
          }}>
                <Card className="bg-card border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] hover:border-primary/40 h-full">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base font-semibold font-default text-foreground">
                        "{kw.keyword}"
                      </CardTitle>
                      <Badge variant="secondary" className="text-xs shrink-0">
                        {kw.intent}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm font-default">
                    <div className="grid grid-cols-2 gap-2 text-xs bg-muted/50 p-2.5 rounded-md">
                      <div>
                        <span className="text-muted-foreground block">Monthly Volume</span>
                        <span className="font-semibold text-foreground">{kw.volume}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Difficulty</span>
                        <span className="font-semibold text-foreground">{kw.difficulty}</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      <span className="font-medium text-foreground">Opportunity:</span> {kw.opportunity}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>

        {/* Local Growth Opportunities Grid */}
        <div className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-10 space-y-2">
            <h3 className="text-2xl font-semibold text-foreground font-default">
              Cheltenham Growth Opportunities
            </h3>
            <p className="text-muted-foreground text-sm font-default">
              Strategic levers I use to help local Cheltenham brands outperform established competitors.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {marketOpportunities.map((op, i) => <motion.div key={op.id} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="h-full">
                <Card className="bg-card border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] hover:border-primary/40 h-full flex flex-col justify-between">
                  <CardHeader className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-xs font-medium border-accent/40 text-accent">
                        {op.tag}
                      </Badge>
                      <Zap className="size-4 text-accent" />
                    </div>
                    <CardTitle className="text-lg font-semibold font-default text-foreground leading-snug">
                      {op.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 flex-1 flex flex-col justify-between font-default">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {op.description}
                    </p>
                    <div className="pt-3 border-t border-border flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Expected Impact:</span>
                      <span className="font-semibold text-primary">{op.impact}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>

        <Separator className="my-12" />

        {/* Nearby Regional Coverage */}
        <div className="mb-16">
          <div className="max-w-2xl mb-8">
            <Badge variant="secondary" className="mb-2 text-xs">
              Regional Coverage
            </Badge>
            <h3 className="text-2xl font-semibold text-foreground font-default">
              Local SEO Consulting Across Neighboring Hubs
            </h3>
            <p className="text-muted-foreground text-sm font-default mt-1">
              Explore localized SEO analysis and strategies for surrounding regional markets.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {neighboringLocations.map((loc, i) => <div key={loc.name} data-index={i}>
                <Link to={loc.path} className="group block p-4 rounded-xl bg-card border border-border hover:border-primary/50 shadow-xs hover:shadow-md transition-[color,background-color,border-color,box-shadow] duration-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-foreground group-hover:text-primary transition-colors text-base font-default">
                      {loc.name}
                    </span>
                    <ArrowUpRight className="size-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground font-default">
                    <span>{loc.focus}</span>
                    <span className="text-muted-foreground/80">{loc.distance}</span>
                  </div>
                </Link>
              </div>)}
          </div>
        </div>

        {/* Direct CTA Banner */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        scale: 0.98
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="rounded-2xl bg-primary text-primary-foreground p-8 sm:p-12 shadow-xl relative overflow-hidden">
          <div className="relative z-10 max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 bg-primary-foreground/10 px-3 py-1 rounded-full text-xs font-medium">
              <Building2 className="size-3.5" />
              <span>Work Directly With Yassine Essaadi</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-semibold font-default text-primary-foreground leading-tight">
              Ready to Win More Search Traffic in Cheltenham?
            </h3>
            <p className="text-primary-foreground/90 text-sm sm:text-base font-default leading-relaxed max-w-2xl">
              I provide tailored SEO strategies without agency bloat or junior hand-offs. Let's analyze your Cheltenham competitors and build a clear path to ranking first.
            </p>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" variant="secondary" className="font-medium text-secondary-foreground shadow-sm hover:bg-secondary/90">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-transparent text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/CaseStudiesPage">
                  Explore My Genuine Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}